/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1715731200000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Wed May 15 2024 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "postgres",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Supported syncing",
          "depth": 0,
          "id": "supported-syncing"
        }, {
          "value": "Connect to PostgreSQL",
          "depth": 0,
          "id": "connect-to-postgresql"
        }, {
          "value": "Required permissions",
          "depth": 1,
          "id": "required-permissions"
        }, {
          "value": "SSH tunneling",
          "depth": 1,
          "id": "ssh-tunneling"
        }, {
          "value": "Sync configuration",
          "depth": 0,
          "id": "sync-configuration"
        }, {
          "value": "Record matching",
          "depth": 1,
          "id": "record-matching"
        }, {
          "value": "Column types",
          "depth": 1,
          "id": "column-types"
        }, {
          "value": "Field mapping",
          "depth": 1,
          "id": "field-mapping"
        }, {
          "value": "Batch size and sleep interval",
          "depth": 1,
          "id": "batch-size-and-sleep-interval"
        }, {
          "value": "Delete behavior",
          "depth": 1,
          "id": "delete-behavior"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      p: "p",
      code: "code",
      a: "a",
      h2: "h2",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      ul: "ul",
      li: "li",
      em: "em",
      h3: "h3",
      img: "img",
      h4: "h4",
      pre: "pre",
      span: "span"
    }, _provideComponents(), props.components), {CommonErrorsPreface, LiveDebugger, SyncAlerts} = _components;
    if (!CommonErrorsPreface) _missingMdxReference("CommonErrorsPreface", true);
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["The minimum supported PostgreSQL version is ", _jsx(_components.code, {
            children: "9.5"
          }), " since Hightouch relies on ", _jsx(_components.a, {
            href: "https://www.postgresql.org/docs/current/sql-insert.html",
            children: _jsx(_components.code, {
              children: "ON CONFLICT"
            })
          }), " for updating rows."]
        })
      }), "\n", _jsx(_components.h2, {
        id: "supported-syncing",
        children: "Supported syncing"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Sync Type"
            }), _jsx(_components.th, {
              children: "Description"
            }), _jsx(_components.th, {
              children: "Supported Sync Modes"
            })]
          })
        }), _jsx(_components.tbody, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Any data set"
            }), _jsx(_components.td, {
              children: "Sync data from any source to a PostgreSQL table"
            }), _jsx(_components.td, {
              children: "Upsert, Insert"
            })]
          })
        })]
      }), "\n", _jsx(_components.h2, {
        id: "connect-to-postgresql",
        children: "Connect to PostgreSQL"
      }), "\n", _jsx(WhitelistDetails, {
        target: "Postgres instance",
        privatelink: {
          supportedIntegrations: ["aws-privatelink", "gcp-psc"]
        }
      }), "\n", _jsxs(_components.p, {
        children: ["Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/destinations",
          children: [_jsx(_components.strong, {
            children: "Destinations"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add destination"
        }), " button. Select ", _jsx(_components.strong, {
          children: "PostgreSQL"
        }), " and click ", _jsx(_components.strong, {
          children: "Continue"
        }), ". If you're not using a ", _jsx(_components.a, {
          href: "#tunneling",
          children: "tunnel"
        }), ", you can then authenticate Hightouch to PostgreSQL by entering the following fields:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Host"
          }), ": The hostname or IP address of your PostgreSQL server. You don't need to include the ", _jsx(_components.code, {
            children: "https://"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Port"
          }), ": The port number of your PostgreSQL server. The default port number is 5432, but yours may be different."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Database"
          }), ": This specifies the database to use when Hightouch executes queries in PostgreSQL. This is different than the host, but your host address might contain your database name."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "User"
          }), ": The user that has access through Hightouch to the database and tables you want to sync to. It's best to ", _jsx(_components.a, {
            href: "https://www.postgresql.org/docs/8.0/sql-createuser.html",
            children: "create a new user"
          }), " specifically for Hightouch access. ", _jsx(_components.em, {
            children: "Do not use the root user."
          })]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Password"
          }), ": The password for the user specified above."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["To ensure your ", _jsx(_components.a, {
          href: "#required-permissions",
          children: "credentials"
        }), " are correct, click ", _jsx(_components.strong, {
          children: "Test connection"
        }), ". This confirms if Hightouch can connect to your database by running a basic ", _jsx(_components.code, {
          children: "SELECT"
        }), " query."]
      }), "\n", _jsx(_components.h3, {
        id: "required-permissions",
        children: "Required permissions"
      }), "\n", _jsx(_components.p, {
        children: "The user you use to authenticate must have the following permissions:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Add, update, and delete (", _jsx(_components.a, {
            href: "#delete-behavior",
            children: "if applicable"
          }), ") rows in your sync's table."]
        }), "\n", _jsxs(_components.li, {
          children: ["View the following tables which are used for gathering metadata to set up the sync:", "\n", _jsxs(_components.ul, {
            children: ["\n", _jsx(_components.li, {
              children: _jsx(_components.code, {
                children: "INFORMATION_SCHEMA.COLUMNS"
              })
            }), "\n", _jsx(_components.li, {
              children: _jsx(_components.code, {
                children: "INFORMATION_SCHEMA.TABLE_CONSTRAINTS"
              })
            }), "\n", _jsx(_components.li, {
              children: _jsx(_components.code, {
                children: "INFORMATION_SCHEMA.CONSTRAINT_COLUMN_USAGE"
              })
            }), "\n"]
          }), "\n"]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "ssh-tunneling",
        children: "SSH tunneling"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch can connect directly to PostgreSQL over the public internet or via an SSH tunnel. Since data is encrypted in transit via TLS, a direct connection is suitable for most use cases. You may need to set up a tunnel if your PostgreSQL instance is on a private network or virtual private cloud (VPC)."
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch supports both standard and reverse SSH tunnels. To learn more about SSH tunneling, refer to Hightouch's ", _jsx(_components.a, {
          href: "/security/ssh-tunneling",
          children: "tunneling documentation"
        }), "."]
      }), "\n", _jsx(_components.h2, {
        id: "sync-configuration",
        children: "Sync configuration"
      }), "\n", _jsxs(_components.p, {
        children: ["Once you've set up your PostgreSQL destination and have a ", _jsx(_components.a, {
          href: "/getting-started/concepts#models",
          children: "model"
        }), " to pull data from, you can set up your sync configuration to begin syncing data. Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/syncs",
          children: [_jsx(_components.strong, {
            children: "Syncs"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add sync"
        }), " button to begin. Then, select the relevant model and the PostgreSQL destination you want to sync to."]
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch supports ", _jsx(_components.strong, {
          children: "Upsert"
        }), " mode, with the option to ", _jsx(_components.a, {
          href: "#delete-behavior",
          children: "delete removed rows"
        }), ", and ", _jsx(_components.strong, {
          children: "Insert"
        }), " mode using the ", _jsx(_components.a, {
          href: "https://www.postgresql.org/docs/current/sql-copy.html",
          children: _jsx(_components.code, {
            children: "COPY FROM STDIN"
          })
        }), "statement. This statement loads queried rows into your table faster than upsert mode. However, using this statement, PostgreSQL rejects the entire batch if any row contains a primary key or unique value that already exists."]
      }), "\n", _jsx(Tip, {
        children: _jsxs(_components.p, {
          children: ["Use insert mode if you are ", _jsx(_components.em, {
            children: "only"
          }), " inserting rows and want to sync your data faster."]
        })
      }), "\n", _jsx(_components.h3, {
        id: "record-matching",
        children: "Record matching"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch requires you select a unique identifier in the table you are syncing to. The model column you select must match a ", _jsx(_components.code, {
          children: "UNIQUE"
        }), ", ", _jsx(_components.code, {
          children: "PRIMARY KEY"
        }), " column within PostgreSQL. If a field that satisfies these conditions still doesn't appear in the dropdown menu, ensure that you assigned the ", _jsx(_components.a, {
          href: "#required-permissions",
          children: "required permissions"
        }), " to the Postgres user."]
      }), "\n", _jsx(_components.p, {
        children: "You can see columns that fit this criteria as available options in record matching section."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-postgres-id-mapping.png",
          alt: "Sync configuration in the Hightouch UI"
        })
      }), "\n", _jsx(_components.p, {
        children: "If there are no fields in the dropdown, you must add a unique type column to your PostgreSQL table. Then, click the refresh icon to access the newly created column."
      }), "\n", _jsx(_components.h3, {
        id: "column-types",
        children: "Column types"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch works out of the box with all standard column types, including:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "BIGINT"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "INT"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "TEXT"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "VARCHAR"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "TIMESTAMPTZ"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "BOOLEAN"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "DECIMAL"
          })
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["If you see type errors, it may be because your model is producing the wrong format. If so, use ", _jsx(_components.a, {
          href: "/models/data-types-casting",
          children: "typecasting"
        }), " to resolve the issue."]
      }), "\n", _jsx(_components.h4, {
        id: "array-support",
        children: "Array support"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch supports arrays of standard JSON objects (JSON or JSONB) but doesn't support arrays of ", _jsxs(_components.a, {
          href: "https://www.postgresql.org/docs/current/xtypes.html",
          children: [_jsx(_components.code, {
            children: "USER-DEFINED"
          }), " type JSON objects"]
        }), "."]
      }), "\n", _jsxs(_components.p, {
        children: ["Because ", _jsx(_components.strong, {
          children: "insert"
        }), " mode uses the ", _jsx(_components.code, {
          children: "COPY FROM STDIN"
        }), " statement, it doesn't support the array of JSONB column type (", _jsx(_components.code, {
          children: "jsonb[]"
        }), "). Instead, use a JSONB column type, which natively supports arrays."]
      }), "\n", _jsx(_components.h3, {
        id: "field-mapping",
        children: "Field mapping"
      }), "\n", _jsx(_components.p, {
        children: "You can sync columns from your model to PostgreSQL fields."
      }), "\n", _jsx(_components.h3, {
        id: "batch-size-and-sleep-interval",
        children: "Batch size and sleep interval"
      }), "\n", _jsx(_components.p, {
        children: "You can tune the number of rows Hightouch upserts or inserts per query based on your needs and database threshold. The default is 1000 rows per batch. You can also optionally enter a custom sleep interval in milliseconds (ms) between batches. This default is 100 ms."
      }), "\n", _jsx(_components.p, {
        children: "If you want to increase the sync's speed, you can increase the batch size and lower the sleep interval. Keep in mind that PostgreSQL fails the entire batch of rows if it detects any erroneous row. If you suspect that you will have many bad rows, don't use a high batch size. To avoid locks, ensure you account for your database's capacity when increasing the batch size."
      }), "\n", _jsx(_components.h3, {
        id: "delete-behavior",
        children: "Delete behavior"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.a, {
          href: "/syncs/overview#delete-behavior",
          children: "delete behavior"
        }), " you select dictates what to do when a row no longer appears in your model's query results. You have the following options:"]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Behavior"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Do nothing"
              })
            }), _jsx(_components.td, {
              children: "Keep the row in PostgreSQL"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Delete row"
              })
            }), _jsx(_components.td, {
              children: "Remove the row from PostgreSQL  entirely"
            })]
          })]
        })]
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(CommonErrorsPreface, {}), "\n", _jsx(_components.h4, {
        id: "canceling-statement-due-to-statement-timeout",
        children: "Canceling statement due to statement timeout"
      }), "\n", _jsx(_components.p, {
        children: "This error occurs when the required execution time of your PostgreSQL query exceeds the timeout limit for the database.\nTo address the error, increase the timeout by executing the following query:"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-json",
          children: ["set statement_timeout = '", _jsx(_components.span, {
            className: "hljs-number",
            children: "300"
          }), " s'; -- ", _jsx(_components.span, {
            className: "hljs-number",
            children: "300"
          }), " seconds", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), " ", _jsx(_components.span, {
            className: "hljs-number",
            children: "5"
          }), " minutes\n"]
        })
      }), "\n", _jsx(_components.p, {
        children: "Be sure to adjust the time to be as long as it takes for your query to execute."
      }), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
